import {
  Customer,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerFilters,
  PaginatedResponse,
  ApiResponse,
  PaginationParams,
  SearchSuggestion
} from '../../types/api'
import { apiClient } from '../../utils/api'

export class CustomerService {
  private readonly basePath = '/customers'

  // CRUD Operations
  async getCustomers(
    filters?: CustomerFilters,
    pagination?: PaginationParams
  ): Promise<ApiResponse<PaginatedResponse<Customer>>> {
    const params = {
      ...filters,
      ...pagination
    }
    return apiClient.get(`${this.basePath}`, params)
  }

  async getCustomer(id: string): Promise<ApiResponse<Customer>> {
    return apiClient.get(`${this.basePath}/${id}`)
  }

  async createCustomer(data: CreateCustomerRequest): Promise<ApiResponse<Customer>> {
    return apiClient.post(`${this.basePath}`, data)
  }

  async updateCustomer(id: string, data: UpdateCustomerRequest): Promise<ApiResponse<Customer>> {
    return apiClient.put(`${this.basePath}/${id}`, data)
  }

  async deleteCustomer(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/${id}`)
  }

  // Search and Suggestions
  async searchCustomers(query: string): Promise<ApiResponse<Customer[]>> {
    return apiClient.get(`${this.basePath}/search`, { q: query })
  }

  async getCustomerSuggestions(query: string): Promise<ApiResponse<SearchSuggestion[]>> {
    return apiClient.get(`${this.basePath}/suggestions`, { q: query })
  }

  async getCustomerByPhone(phone: string): Promise<ApiResponse<Customer | null>> {
    return apiClient.get(`${this.basePath}/phone/${phone}`)
  }

  async getCustomerByTaxNumber(taxNumber: string): Promise<ApiResponse<Customer | null>> {
    return apiClient.get(`${this.basePath}/tax-number/${taxNumber}`)
  }

  // Customer Analytics
  async getCustomerStatistics(id: string, period?: string): Promise<ApiResponse<{
    total_invoices: number
    total_amount: number
    paid_amount: number
    outstanding_amount: number
    average_order_value: number
    last_purchase_date?: string
    payment_history: Array<{
      date: string
      amount: number
      status: string
    }>
  }>> {
    return apiClient.get(`${this.basePath}/${id}/statistics`, { period })
  }

  async getTopCustomers(limit: number = 10, period?: string): Promise<ApiResponse<Array<{
    customer: Customer
    total_amount: number
    invoice_count: number
  }>>> {
    return apiClient.get(`${this.basePath}/top`, { limit, period })
  }

  // Customer Invoices
  async getCustomerInvoices(id: string, filters?: {
    status?: string
    date_from?: string
    date_to?: string
  }): Promise<ApiResponse<any[]>> {
    return apiClient.get(`${this.basePath}/${id}/invoices`, filters)
  }

  async getCustomerOutstandingAmount(id: string): Promise<ApiResponse<{
    total_outstanding: number
    overdue_amount: number
    current_amount: number
  }>> {
    return apiClient.get(`${this.basePath}/${id}/outstanding`)
  }

  // Customer Status Management
  async activateCustomer(id: string): Promise<ApiResponse<Customer>> {
    return apiClient.patch(`${this.basePath}/${id}/activate`)
  }

  async deactivateCustomer(id: string): Promise<ApiResponse<Customer>> {
    return apiClient.patch(`${this.basePath}/${id}/deactivate`)
  }

  // Credit Management
  async updateCreditLimit(id: string, creditLimit: number): Promise<ApiResponse<Customer>> {
    return apiClient.patch(`${this.basePath}/${id}/credit-limit`, { credit_limit: creditLimit })
  }

  async getCreditStatus(id: string): Promise<ApiResponse<{
    credit_limit: number
    used_credit: number
    available_credit: number
    credit_utilization: number
  }>> {
    return apiClient.get(`${this.basePath}/${id}/credit-status`)
  }

  // Bulk Operations
  async bulkUpdate(ids: string[], data: Partial<UpdateCustomerRequest>): Promise<ApiResponse<{
    updated: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.put(`${this.basePath}/bulk`, { ids, data })
  }

  async bulkDelete(ids: string[]): Promise<ApiResponse<{
    deleted: number
    failed: Array<{ id: string, error: string }>
  }>> {
    return apiClient.delete(`${this.basePath}/bulk`, { ids })
  }

  async exportCustomers(filters?: CustomerFilters, format: 'excel' | 'csv' = 'excel'): Promise<ApiResponse<{ url: string }>> {
    return apiClient.post(`${this.basePath}/export`, { filters, format })
  }

  // Import
  async importCustomers(file: File): Promise<ApiResponse<{
    imported: number
    failed: number
    errors?: Array<{ row: number, errors: string[] }>
  }>> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`${this.basePath}/import`, formData)
  }

  // Recent Customers
  async getRecentCustomers(limit: number = 10): Promise<ApiResponse<Customer[]>> {
    return apiClient.get(`${this.basePath}/recent`, { limit })
  }

  // Customer Communication
  async sendMessage(id: string, messageData: {
    type: 'sms' | 'email' | 'whatsapp'
    subject?: string
    message: string
    template_id?: string
  }): Promise<ApiResponse<{ sent: boolean, message_id?: string }>> {
    return apiClient.post(`${this.basePath}/${id}/message`, messageData)
  }

  // Address Management
  async updateAddress(id: string, address: any): Promise<ApiResponse<Customer>> {
    return apiClient.patch(`${this.basePath}/${id}/address`, { address })
  }

  async validateAddress(address: any): Promise<ApiResponse<{
    valid: boolean
    suggestions?: any[]
    errors?: string[]
  }>> {
    return apiClient.post(`${this.basePath}/validate-address`, { address })
  }
}

// Create and export service instance
export const customerService = new CustomerService() 